import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useApiData } from "../lib/hooks";
import { ErrorMessage } from "../components/ErrorMessage";
import { Field } from "../components/Field";
import { Loading } from "../components/Loading";
import { BOW_STYLE_LABEL } from "../lib/bowStyles";

// Bow styles come from shared/enums.json via lib/bowStyles.js (#2048); this
// page only displays the current one, set on the equipment loadout.
// Mirrors the backend Classification enum.
const CLASSIFICATIONS = [
  ["unclassified", "Unclassified"],
  ["third_class", "3rd Class"],
  ["second_class", "2nd Class"],
  ["first_class", "1st Class"],
  ["bowman", "Bowman"],
  ["master_bowman", "Master Bowman"],
];

// Competition category (age class + gender). Setting these lets the round wizard
// pre-fill the right shooting distance / division from a round's distance_sets.
// Values mirror the backend age_class/gender contract.
const AGE_CLASSES = [
  ["senior", "Senior"],
  ["u21", "U21"],
  ["u18", "U18"],
  ["u15", "U15"],
  ["u13", "U13"],
  ["master", "Master (50+)"],
];
const GENDERS = [
  ["men", "Men"],
  ["women", "Women"],
];

// Curated list of IANA timezone IDs. Value "" means "use device timezone".
const TIMEZONES = [
  { value: "", label: "— Use device timezone —" },
  { value: "Pacific/Honolulu",    label: "Hawaii (UTC-10)" },
  { value: "America/Anchorage",   label: "Alaska (UTC-9)" },
  { value: "America/Los_Angeles", label: "Pacific (UTC-8/-7)" },
  { value: "America/Denver",      label: "Mountain (UTC-7/-6)" },
  { value: "America/Chicago",     label: "Central (UTC-6/-5)" },
  { value: "America/New_York",    label: "Eastern (UTC-5/-4)" },
  { value: "America/Halifax",     label: "Atlantic (UTC-4/-3)" },
  { value: "America/Sao_Paulo",   label: "Brasília (UTC-3)" },
  { value: "Atlantic/Azores",     label: "Azores (UTC-1/0)" },
  { value: "Europe/London",       label: "London (UTC+0/+1)" },
  { value: "Europe/Paris",        label: "Central Europe (UTC+1/+2)" },
  { value: "Europe/Helsinki",     label: "Eastern Europe (UTC+2/+3)" },
  { value: "Europe/Moscow",       label: "Moscow (UTC+3)" },
  { value: "Asia/Dubai",          label: "Dubai (UTC+4)" },
  { value: "Asia/Karachi",        label: "Pakistan (UTC+5)" },
  { value: "Asia/Kolkata",        label: "India (UTC+5:30)" },
  { value: "Asia/Dhaka",          label: "Bangladesh (UTC+6)" },
  { value: "Asia/Bangkok",        label: "SE Asia (UTC+7)" },
  { value: "Asia/Shanghai",       label: "China (UTC+8)" },
  { value: "Asia/Tokyo",          label: "Japan (UTC+9)" },
  { value: "Australia/Perth",     label: "W. Australia (UTC+8)" },
  { value: "Australia/Adelaide",  label: "S. Australia (UTC+9:30/+10:30)" },
  { value: "Australia/Sydney",    label: "E. Australia (UTC+10/+11)" },
  { value: "Pacific/Auckland",    label: "New Zealand (UTC+12/+13)" },
];

// Fields the owner may edit; the rest of the profile is read-only.
const EDITABLE = [
  "name",
  "handle",
  "club",
  "bow_style",
  "usa_archery_classification",
  "age_class",
  "gender",
  "is_public",
  "timezone",
];
// Enum fields: empty string means "no value chosen" — don't send to API.
// timezone is NOT an enum — "" means "use device timezone" and IS a valid value.
const ENUM_FIELDS = new Set(["bow_style", "usa_archery_classification"]);
// Nullable category fields: blank "—" IS a valid value (clears the category),
// so we DO send it — but as an explicit null, not an empty string.
const NULLABLE_FIELDS = new Set(["age_class", "gender"]);

function friendly(message) {
  return String(message || "Something went wrong.").replace(/^\d{3}\s+/, "");
}

function cap(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function toForm(p) {
  return {
    name: p.name ?? "",
    handle: p.handle ?? "",
    club: p.club ?? "",
    bow_style: p.bow_style ?? "",
    usa_archery_classification: p.usa_archery_classification ?? "",
    age_class: p.age_class ?? "",
    gender: p.gender ?? "",
    is_public: !!p.is_public,
    timezone: p.timezone ?? "",
  };
}

// Build a PATCH payload of just the fields the owner actually changed.
function changedFields(profile, form) {
  const out = {};
  for (const f of EDITABLE) {
    const current = f === "is_public" ? !!profile[f] : profile[f] ?? "";
    if (form[f] === current) continue;
    // The API has no way to clear an enum back to unset — skip empty ones.
    if (ENUM_FIELDS.has(f) && form[f] === "") continue;
    // Category fields are nullable — a blank "—" clears them, sent as null.
    if (NULLABLE_FIELDS.has(f) && form[f] === "") { out[f] = null; continue; }
    out[f] = form[f];
  }
  return out;
}

export function Profile() {
  const { activeArcher, archers, refresh } = useAuth();
  const archerId = activeArcher?.id;
  const role = archers.find((m) => m.archer.id === archerId)?.role;
  const canEdit = role === "full";

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data: fetchedProfile, loading, error: fetchError } = useApiData(
    () => api.archer(archerId),
    [archerId],
    { enabled: archerId != null },
  );

  useEffect(() => {
    if (!fetchedProfile) return;
    setProfile(fetchedProfile);
    setForm(toForm(fetchedProfile));
  }, [fetchedProfile]);

  const error = actionError ?? (fetchError ? friendly(fetchError) : null);

  function setField(name, value) {
    setForm((f) => ({ ...f, [name]: value }));
    setSaved(false);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setActionError(null);
    setSaving(true);
    try {
      const updated = await api.updateArcher(archerId, changedFields(profile, form));
      setProfile(updated);
      setForm(toForm(updated));
      setSaved(true);
      // The switcher shows the archer's name/handle — keep it fresh.
      refresh();
    } catch (e) {
      setActionError(friendly(e.message));
    } finally {
      setSaving(false);
    }
  }

  async function onPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setActionError(null);
    setUploading(true);
    try {
      const updated = await api.uploadArcherPhoto(archerId, file);
      setProfile(updated);
      setForm(toForm(updated));
    } catch (err) {
      setActionError(friendly(err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  return (
    <main className="main">
      <div className="main-head">
        <div>
          <h1>Profile</h1>
          <div className="greeting">
            {canEdit ? "Your archer profile." : "Viewing — read only."}
          </div>
        </div>
      </div>

      {loading && <Loading compact />}

      {!loading && profile && (
        <div className="panel profile-panel">
          <form className="profile-body" onSubmit={onSubmit}>
            <ErrorMessage message={error} />

            <div className="profile-photo-row">
              <div className="profile-photo">
                {profile.photo_url ? (
                  <img src={profile.photo_url} alt={profile.name} />
                ) : (
                  <span className="profile-photo-fallback">{profile.initials}</span>
                )}
              </div>
              {canEdit && (
                <label className="btn profile-photo-btn">
                  {uploading ? "Uploading…" : "Change photo"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={onPhoto}
                    disabled={uploading}
                    hidden
                  />
                </label>
              )}
            </div>

            <Field label="Name">
              <input
                className="input"
                value={form.name}
                disabled={!canEdit}
                onChange={(e) => setField("name", e.target.value)}
              />
            </Field>

            <Field label="Handle">
              <input
                className="input"
                value={form.handle}
                disabled={!canEdit}
                onChange={(e) => setField("handle", e.target.value)}
              />
            </Field>

            <Field label="Club">
              <input
                className="input"
                value={form.club}
                disabled={!canEdit}
                onChange={(e) => setField("club", e.target.value)}
              />
            </Field>

            <Field label="Bow style">
              <span className="profile-field-note">
                Set on your <a href="/equipment" className="link">equipment loadout</a>
                {form.bow_style
                  ? ` (currently: ${BOW_STYLE_LABEL[form.bow_style] ?? cap(form.bow_style)})`
                  : ""}
              </span>
            </Field>

            <Field label="USA Archery classification">
              <select
                className="input"
                value={form.usa_archery_classification}
                disabled={!canEdit}
                onChange={(e) =>
                  setField("usa_archery_classification", e.target.value)
                }
              >
                <option value="">—</option>
                {CLASSIFICATIONS.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Competition category — age class">
              <select
                className="input"
                value={form.age_class}
                disabled={!canEdit}
                onChange={(e) => setField("age_class", e.target.value)}
              >
                <option value="">—</option>
                {AGE_CLASSES.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Competition category — gender">
              <select
                className="input"
                value={form.gender}
                disabled={!canEdit}
                onChange={(e) => setField("gender", e.target.value)}
              >
                <option value="">—</option>
                {GENDERS.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Timezone">
              <select
                className="input"
                value={form.timezone}
                disabled={!canEdit}
                onChange={(e) => setField("timezone", e.target.value)}
              >
                {TIMEZONES.map(({ value, label }) => (
                  <option key={value || "device"} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>

            <label className="profile-toggle">
              <input
                type="checkbox"
                checked={form.is_public}
                disabled={!canEdit}
                onChange={(e) => setField("is_public", e.target.checked)}
              />
              <span>
                Public profile — visible to archers outside your workspace
              </span>
            </label>

            {canEdit && (
              <div className="profile-actions">
                <button type="submit" className="btn primary" disabled={saving}>
                  {saving ? "Saving…" : "Save changes"}
                </button>
                {saved && <span className="profile-saved">Saved ✓</span>}
              </div>
            )}
          </form>
        </div>
      )}
    </main>
  );
}
